import { IconImage } from "common/components/SerializedApp";
import * as React from "react";
import tw from "twin.macro";
import { WireTab } from "common/wire";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { Appearance } from "common/generator/components/button-appearance";
import { useColorFromSeed } from "sharedUI/ui/views/user-preview-icon/user-preview-icon";
import chroma from "chroma-js";
import { useResponsiveSizeClass } from "webapp/wire-renderers/desktop-wire-app";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import { WireContainer, SectionStyle } from "webapp/ui/views/wire-container/wire-container";
import { styled } from "webapp/styled-components";

interface Props {
    readonly tabs: readonly WireTab[];
    readonly selected?: WireTab;
    readonly onSelectionChange?: (tab: WireTab) => void;
    readonly title: string;
    readonly icon?: IconImage;
    readonly className?: string;
    readonly signedInUserEmail?: string;
    readonly signedInUserName?: string;
    readonly onUserButtonPressed?: () => void;
    readonly onSignInPressed?: () => void;
}

const PopupMenuStyle = styled.div`
    &.open {
        ${tw`translate-y-0 opacity-100 pointer-events-auto`};
    }

    &.close {
        ${tw`-translate-y-2 opacity-0 pointer-events-none`};
    }
`;

export const DesktopNavBar: React.VFC<Props> = p => {
    const {
        title,
        tabs,
        className,
        selected,
        onSelectionChange,
        onUserButtonPressed,
        onSignInPressed,
        signedInUserEmail,
        signedInUserName,
    } = p;

    const [showMenu, setShowMenu] = React.useState(false);

    const username = signedInUserName ?? signedInUserEmail ?? "?";

    const colorPreview = useColorFromSeed(username);
    const background = chroma(colorPreview).brighten(3).css();

    const navRegion = (
        <>
            {tabs.map((t, index) => (
                <button
                    onClick={() => {
                        setShowMenu(false);
                        onSelectionChange?.(t);
                    }}
                    tw="px-4 rounded-lg h-9 transition-all duration-200 cursor-pointer whitespace-nowrap bg-n0A
                        opacity-80 hover:(opacity-100) focus-visible:(ring-2 ring-offset-1 ring-n0A) text-left
                        gp-sm:text-center"
                    key={index}
                    css={[selected === t && tw`background[rgba(0, 0, 0, .1)] opacity-100`]}>
                    {t.title}
                </button>
            ))}
        </>
    );

    const sizeClass = useResponsiveSizeClass();

    if (sizeClass === undefined) {
        return (
            <>
                <WireContainer background={SectionStyle.Accent} tw="box-shadow[0 0 0 1px #ffffff33]">
                    <header className={className} tw="h-14 flex flex-col text-white w-full z-20">
                        <div tw="flex justify-between">
                            <button tw="p-2 -ml-2" onClick={() => setShowMenu(cv => !cv)}>
                                {!showMenu && <GlideIcon kind="monotone" icon="mt-menu" iconSize={20} />}
                                {showMenu && <GlideIcon kind="stroke" icon="st-close" iconSize={20} />}
                            </button>
                            <div
                                tw="h-14 flex flex-shrink items-center font-extrabold text-xl mr-2 overflow-hidden
                                    whitespace-nowrap overflow-ellipsis min-w-0">
                                {title}
                            </div>
                            <div tw="flex-shrink-0 self-center flex justify-center items-center">
                                {onSignInPressed !== undefined && (
                                    <WireButton
                                        onClick={onSignInPressed}
                                        appearance={Appearance.Bordered}
                                        tw="flex-shrink-0 mr-0.5">
                                        Sign In
                                    </WireButton>
                                )}
                                {onUserButtonPressed !== undefined && (
                                    <button
                                        onClick={onUserButtonPressed}
                                        style={{
                                            color: colorPreview,
                                            backgroundColor: background,
                                        }}
                                        tw="flex relative capitalize justify-center items-center text-base w-8 h-8
                                            flex-shrink-0 cursor-pointer rounded-full mr-1 after:(content absolute
                                            -inset-0.5 rounded-full pointer-events-none transition-shadow)
                                            focus-visible:after:(ring-2 ring-white)">
                                        {username[0]}
                                    </button>
                                )}
                            </div>
                        </div>
                    </header>
                    <PopupMenuStyle
                        className={showMenu ? "open" : "close"}
                        tw="absolute top-full left-0 right-0 transform transition-all">
                        <WireContainer background={SectionStyle.Accent} tw="mt-0! box-shadow[inset 0 1px #ffffff33]">
                            <nav className="scrollbar-hide" tw="flex flex-col py-2 items-stretch text-white">
                                {navRegion}
                                <div tw="w-10 flex-shrink" />
                            </nav>
                        </WireContainer>
                        <div tw="shadow-2xl-dark h-5 -mt-5 pointer-events-none" />
                    </PopupMenuStyle>
                </WireContainer>
            </>
        );
    }

    return (
        <WireContainer background={SectionStyle.Accent} tw="box-shadow[0 0 0 1px #ffffff33]">
            <header className={className} tw="overflow-hidden flex color[white] w-full relative">
                <div tw="flex flex-wrap flex-shrink min-w-0">
                    <div tw="h-14 flex items-center flex-shrink-0 font-extrabold text-xl mr-6">{title}</div>
                    <nav
                        className="scrollbar-hide"
                        tw="h-14 flex px-0.5 flex-shrink min-w-0 overflow-x-auto items-center">
                        {navRegion}
                        <div tw="w-10 flex-shrink" />
                    </nav>
                </div>
                <div tw="flex-shrink-0 self-center absolute right-1 top-0 h-14 flex justify-center items-center">
                    {onSignInPressed !== undefined && (
                        <WireButton
                            onClick={onSignInPressed}
                            appearance={Appearance.Bordered}
                            tw="flex-shrink-0 mr-0.5">
                            Sign In
                        </WireButton>
                    )}
                    {onUserButtonPressed !== undefined && (
                        <button
                            onClick={onUserButtonPressed}
                            style={{
                                color: colorPreview,
                                backgroundColor: background,
                            }}
                            tw="flex relative capitalize justify-center items-center text-base w-8 h-8 flex-shrink-0
                                cursor-pointer rounded-full mr-1 after:(content absolute -inset-0.5 rounded-full
                                pointer-events-none transition-shadow) focus-visible:after:(ring-2 ring-white)">
                            {username[0]}
                        </button>
                    )}
                </div>
            </header>
        </WireContainer>
    );
};
